import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

/**
 * Job entity matching the backend data model.
 */
export interface Job {
  id: string;
  tenantId: string;
  clientId: string;
  clientName: string;
  practiceEntityId: string | null;
  jobCode: string | null;
  jobType: string;
  entityTypeId: string | null;
  workflowTemplateId: string | null;
  workflowTemplateName: string | null;
  title: string;
  description: string | null;
  status: JobStatus;
  priority: JobPriority;
  currentStageId: string | null;
  currentStageName: string | null;
  assignedPartnerId: string | null;
  assignedPartnerName: string | null;
  assignedManagerId: string | null;
  assignedManagerName: string | null;
  assignedAccountantId: string | null;
  assignedAccountantName: string | null;
  budgetAmount: number | null;
  estimatedHours: number | null;
  startDate: string | null;
  dueDate: string | null;
  completedDate: string | null;
  cancellationReason: string | null;
  isRecurring: boolean;
  recurrenceFrequency: string | null;
  recurrenceNextDate: string | null;
  progressPercentage: number;
  isOverdue: boolean;
  createdAt: string;
  updatedAt: string;
}

export type JobStatus =
  | 'not_started'
  | 'in_progress'
  | 'on_hold'
  | 'waiting_on_client'
  | 'in_review'
  | 'complete'
  | 'cancelled';

export type JobPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface JobStage {
  id: string;
  jobId: string;
  templateStageId: string | null;
  name: string;
  displayOrder: number;
  status: 'pending' | 'active' | 'completed';
  startedAt: string | null;
  completedAt: string | null;
}

export interface JobTask {
  id: string;
  jobId: string;
  stageId: string;
  title: string;
  description: string | null;
  isMandatory: boolean;
  isCompleted: boolean;
  assignedToId: string | null;
  assignedToName: string | null;
  assignedToClient: boolean;
  clientDueDate: string | null;
  assigneeRole: string | null;
  taskType: string;
  documentId: string | null;
  completedAt: string | null;
  completedById: string | null;
  displayOrder: number;
}

export interface JobDetail extends Job {
  stages: JobStage[];
  tasks: JobTask[];
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string | null;
  jobType: string;
  isActive: boolean;
  stages: WorkflowTemplateStage[];
}

export interface WorkflowTemplateStage {
  id: string;
  name: string;
  displayOrder: number;
  isFinal: boolean;
  tasks: WorkflowTemplateTask[];
}

export interface WorkflowTemplateTask {
  id: string;
  title: string;
  description: string | null;
  isMandatory: boolean;
  displayOrder: number;
  defaultAssigneeRole: string | null;
  estimatedHours: number | null;
}

export interface StaffWorkload {
  userId: string;
  userName: string;
  role: string;
  totalJobs: number;
  activeJobs: number;
  completedJobs: number;
  overdueJobs: number;
  totalEstimatedHours: number;
  totalLoggedHours: number;
  utilizationPercentage: number;
  capacityHoursPerWeek: number;
}

export interface KanbanColumn {
  stageId: string;
  stageName: string;
  displayOrder: number;
  jobs: Job[];
}

/** Query params for listing jobs */
export interface JobsListParams {
  page?: number;
  pageSize?: number;
  status?: JobStatus | '';
  priority?: JobPriority | '';
  assigneeId?: string;
  clientId?: string;
  groupId?: string;
  jobType?: string;
  practiceEntityId?: string;
  assignedRole?: 'partner' | 'manager' | 'accountant' | '';
  assignedUserId?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  search?: string;
  isOverdue?: boolean;
  futureRecurring?: boolean;
  showAll?: boolean;
}

/** Paginated response */
interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Create job request */
export interface CreateJobRequest {
  clientId: string;
  entityTypeId?: string;
  practiceEntityId?: string;
  jobType?: string;
  workflowTemplateId?: string;
  /** Optional: mandatory-ness is controlled by the tenant's form builder. */
  title?: string;
  description?: string;
  priority?: JobPriority;
  assignedPartnerId?: string;
  assignedManagerId?: string;
  assignedAccountantId?: string;
  budgetAmount?: number;
  estimatedHours?: number;
  startDate?: string;
  dueDate?: string;
  isRecurring?: boolean;
  recurrenceFrequency?: string;
  recurrenceNextDate?: string;
  dynamicFields?: Record<string, unknown>;
  formData?: Record<string, unknown>;
}

/** Update job request */
export interface UpdateJobRequest {
  clientId?: string;
  title?: string;
  description?: string;
  status?: JobStatus;
  priority?: JobPriority;
  workflowTemplateId?: string | null;
  assignedPartnerId?: string | null;
  assignedManagerId?: string | null;
  assignedAccountantId?: string | null;
  budgetAmount?: number | null;
  estimatedHours?: number | null;
  startDate?: string | null;
  dueDate?: string | null;
  isRecurring?: boolean;
  recurrenceFrequency?: string | null;
  recurrenceNextDate?: string | null;
  cancellationReason?: string;
  formData?: Record<string, unknown>;
}

/** Stage transition request */
export interface TransitionStageRequest {
  jobId: string;
  targetStageId: string;
}

// Query keys
const jobKeys = {
  all: ['jobs'] as const,
  lists: () => [...jobKeys.all, 'list'] as const,
  list: (params: JobsListParams) => [...jobKeys.lists(), params] as const,
  details: () => [...jobKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobKeys.details(), id] as const,
  kanban: (params?: Record<string, string>) => [...jobKeys.all, 'kanban', params] as const,
  workload: () => [...jobKeys.all, 'workload'] as const,
  templates: () => [...jobKeys.all, 'templates'] as const,
};

/**
 * Hook to fetch paginated list of jobs with filtering and sorting.
 * Validates: Requirements 8.17
 */
export function useJobsList(params: JobsListParams = {}) {
  return useQuery({
    queryKey: jobKeys.list(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.set('page', String(params.page));
      if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));
      if (params.status) queryParams.set('status', params.status);
      if (params.priority) queryParams.set('priority', params.priority);
      if (params.assigneeId) queryParams.set('assigned_accountant_id', params.assigneeId);
      if (params.clientId) queryParams.set('client_id', params.clientId);
      if (params.groupId) queryParams.set('group_id', params.groupId);
      if (params.jobType) queryParams.set('job_type', params.jobType);
      if (params.practiceEntityId) queryParams.set('practice_entity_id', params.practiceEntityId);
      if (params.sortBy) queryParams.set('sortBy', params.sortBy);
      if (params.sortDirection) queryParams.set('sortDirection', params.sortDirection);
      if (params.search) queryParams.set('search', params.search);
      if (params.isOverdue !== undefined) queryParams.set('is_overdue', String(params.isOverdue));
      if (params.futureRecurring) queryParams.set('future_recurring', 'true');
      if (params.assignedRole) queryParams.set('assigned_role', params.assignedRole);
      if (params.assignedUserId) queryParams.set('assigned_user_id', params.assignedUserId);
      if (params.showAll) queryParams.set('show_all', 'true');

      const response = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        pagination: { page: number; pageSize: number; total: number };
      }>(`/jobs?${queryParams.toString()}`);

      const raw = response.data;
      const items: Job[] = (raw.data ?? []).map((j) => ({
        id: j.id as string,
        tenantId: (j.tenant_id || '') as string,
        clientId: (j.client_id || '') as string,
        clientName: (j.client_name || '') as string,
        practiceEntityId: (j.practice_entity_id || null) as string | null,
        jobCode: (j.job_code || null) as string | null,
        jobType: (j.job_type || '') as string,
        entityTypeId: (j.entity_type_id || null) as string | null,
        workflowTemplateId: (j.workflow_template_id || null) as string | null,
        workflowTemplateName: (j.workflow_template_name || j.workflowTemplateName || null) as
          | string
          | null,
        title: (j.title || '') as string,
        description: (j.description || null) as string | null,
        status: (j.status || 'not_started') as JobStatus,
        priority: (j.priority || 'medium') as JobPriority,
        currentStageId: (j.current_stage_id || null) as string | null,
        currentStageName: (j.current_stage_name || null) as string | null,
        assignedPartnerId: (j.assigned_partner_id || null) as string | null,
        assignedPartnerName: (j.assigned_partner_name || null) as string | null,
        assignedManagerId: (j.assigned_manager_id || null) as string | null,
        assignedManagerName: (j.assigned_manager_name || null) as string | null,
        assignedAccountantId: (j.assigned_accountant_id || null) as string | null,
        assignedAccountantName: (j.assigned_accountant_name || null) as string | null,
        budgetAmount: (j.budget_amount || null) as number | null,
        estimatedHours: (j.estimated_hours || null) as number | null,
        startDate: (j.start_date || null) as string | null,
        dueDate: (j.due_date || null) as string | null,
        completedDate: (j.completed_date || null) as string | null,
        cancellationReason: (j.cancellation_reason || null) as string | null,
        isRecurring: (j.is_recurring || false) as boolean,
        recurrenceFrequency: (j.recurrence_frequency || null) as string | null,
        recurrenceNextDate: (j.recurrence_next_date || null) as string | null,
        progressPercentage: (j.progress_percentage || 0) as number,
        isOverdue: (j.is_overdue || false) as boolean,
        createdAt: (j.created_at || '') as string,
        updatedAt: (j.updated_at || '') as string,
      }));

      return {
        items,
        total: raw.pagination?.total ?? items.length,
        page: raw.pagination?.page ?? 1,
        pageSize: raw.pagination?.pageSize ?? 25,
      } as PaginatedResponse<Job>;
    },
  });
}

/**
 * Hook to fetch a single job with stages and tasks.
 */
export function useJobDetail(jobId: string) {
  return useQuery({
    queryKey: jobKeys.detail(jobId),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
        `/jobs/${jobId}`,
      );
      const j = response.data.data;

      const stages = ((j.stages as Record<string, unknown>[]) || []).map((s) => ({
        id: s.id as string,
        jobId: (s.job_id || s.jobId || '') as string,
        templateStageId: (s.template_stage_id || s.templateStageId || null) as string | null,
        name: (s.name || '') as string,
        displayOrder: (s.display_order ?? s.displayOrder ?? 0) as number,
        isFinal: (s.is_final ?? s.isFinal ?? false) as boolean,
        isCurrent: (s.is_current ?? s.isCurrent ?? false) as boolean,
        status: (s.started_at || s.startedAt
          ? s.completed_at || s.completedAt
            ? 'completed'
            : 'active'
          : 'pending') as 'pending' | 'active' | 'completed',
        startedAt: (s.started_at || s.startedAt || null) as string | null,
        completedAt: (s.completed_at || s.completedAt || null) as string | null,
        tasks: ((s.tasks as Record<string, unknown>[]) || []).map((t) => ({
          id: t.id as string,
          jobId: jobId,
          stageId: (t.stage_id || t.stageId || '') as string,
          title: (t.title || '') as string,
          description: (t.description || null) as string | null,
          isMandatory: (t.is_mandatory ?? t.isMandatory ?? false) as boolean,
          isCompleted: (t.is_completed ?? t.isCompleted ?? false) as boolean,
          completedAt: (t.completed_at || t.completedAt || null) as string | null,
          completedById: (t.completed_by_id || t.completedById || null) as string | null,
          assignedToId: (t.assigned_to_id || t.assignedToId || null) as string | null,
          assignedToName: (t.assigned_to_name || t.assignedToName || null) as string | null,
          assignedToClient: (t.assigned_to_client ?? t.assignedToClient ?? false) as boolean,
          clientDueDate: (t.client_due_date || t.clientDueDate || null) as string | null,
          assigneeRole: (t.assignee_role || t.assigneeRole || null) as string | null,
          taskType: (t.task_type || t.taskType || 'standard') as string,
          documentId: (t.document_id || t.documentId || null) as string | null,
          displayOrder: (t.display_order ?? t.displayOrder ?? 0) as number,
        })),
      }));

      const allTasks = stages.flatMap((s) => s.tasks);

      const job: JobDetail = {
        id: j.id as string,
        tenantId: (j.tenant_id || j.tenantId || '') as string,
        clientId: (j.client_id || j.clientId || '') as string,
        clientName: (j.client_name || j.clientName || '') as string,
        practiceEntityId: (j.practice_entity_id || j.practiceEntityId || null) as string | null,
        jobCode: (j.job_code || j.jobCode || null) as string | null,
        jobType: (j.job_type || j.jobType || '') as string,
        entityTypeId: (j.entity_type_id || j.entityTypeId || null) as string | null,
        workflowTemplateId: (j.workflow_template_id || j.workflowTemplateId || null) as
          | string
          | null,
        workflowTemplateName: (j.workflow_template_name || j.workflowTemplateName || null) as
          | string
          | null,
        title: (j.title || '') as string,
        description: (j.description || null) as string | null,
        status: (j.status || 'not_started') as JobStatus,
        priority: (j.priority || 'medium') as JobPriority,
        currentStageId: (j.current_stage_id || j.currentStageId || null) as string | null,
        currentStageName: (j.current_stage_name || j.currentStageName || null) as string | null,
        assignedPartnerId: (j.assigned_partner_id || j.assignedPartnerId || null) as string | null,
        assignedPartnerName: (j.assigned_partner_name || j.assignedPartnerName || null) as
          | string
          | null,
        assignedManagerId: (j.assigned_manager_id || j.assignedManagerId || null) as string | null,
        assignedManagerName: (j.assigned_manager_name || j.assignedManagerName || null) as
          | string
          | null,
        assignedAccountantId: (j.assigned_accountant_id || j.assignedAccountantId || null) as
          | string
          | null,
        assignedAccountantName: (j.assigned_accountant_name || j.assignedAccountantName || null) as
          | string
          | null,
        budgetAmount: (j.budget_amount ?? j.budgetAmount ?? null) as number | null,
        estimatedHours: (j.estimated_hours ?? j.estimatedHours ?? null) as number | null,
        startDate: (j.start_date || j.startDate || null) as string | null,
        dueDate: (j.due_date || j.dueDate || null) as string | null,
        completedDate: (j.completed_date || j.completedDate || null) as string | null,
        cancellationReason: (j.cancellation_reason || j.cancellationReason || null) as
          | string
          | null,
        isRecurring: (j.is_recurring ?? j.isRecurring ?? false) as boolean,
        recurrenceFrequency: (j.recurrence_frequency || j.recurrenceFrequency || null) as
          | string
          | null,
        recurrenceNextDate: (j.recurrence_next_date || j.recurrenceNextDate || null) as
          | string
          | null,
        progressPercentage: (j.progress_percentage ?? j.progressPercentage ?? 0) as number,
        isOverdue: (j.is_overdue ?? j.isOverdue ?? false) as boolean,
        createdAt: (j.created_at || j.createdAt || '') as string,
        updatedAt: (j.updated_at || j.updatedAt || '') as string,
        stages,
        tasks: allTasks,
      };

      // Attach form_data and customFields from raw response (not part of typed Job interface)
      (job as unknown as Record<string, unknown>).formData = j.form_data ?? j.formData ?? null;
      (job as unknown as Record<string, unknown>).customFields =
        j.customFields ?? j.custom_fields ?? null;

      return job;
    },
    enabled: !!jobId,
  });
}

/**
 * Hook to fetch jobs in Kanban board format grouped by stage.
 * Validates: Requirements 8.8
 */
export function useJobsKanban(params?: Record<string, string>) {
  return useQuery({
    queryKey: jobKeys.kanban(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams(params);
      const response = await apiClient.get<ApiResponse<KanbanColumn[]>>(
        `/jobs/kanban?${queryParams.toString()}`,
      );
      return response.data.data;
    },
  });
}

/**
 * Hook to fetch staff workload dashboard data.
 * Validates: Requirements 8.20
 */
export function useWorkloadDashboard() {
  return useQuery({
    queryKey: jobKeys.workload(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<StaffWorkload[]>>('/jobs/workload');
      return response.data.data;
    },
  });
}

/**
 * Hook to fetch workflow templates for job creation.
 */
export function useWorkflowTemplates() {
  return useQuery({
    queryKey: jobKeys.templates(),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown>[] }>(
        '/settings/workflow-templates',
      );
      const raw = response.data.data || [];
      return raw.map(
        (t): WorkflowTemplate => ({
          id: t.id as string,
          name: (t.name || '') as string,
          description: (t.description || '') as string,
          jobType: (t.job_type || t.jobType || '') as string,
          isActive: (t.is_active ?? t.isActive ?? true) as boolean,
          stages: [],
        }),
      );
    },
  });
}

/**
 * Hook to create a new job.
 */
export function useCreateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateJobRequest) => {
      const payload: Record<string, unknown> = {
        client_id: data.clientId,
        entity_type_id: data.entityTypeId || undefined,
        practice_entity_id: data.practiceEntityId || null,
        job_type: data.jobType,
        workflow_template_id: data.workflowTemplateId,
        title: data.title,
        description: data.description || undefined,
        priority: data.priority || undefined,
        assigned_partner_id: data.assignedPartnerId || undefined,
        assigned_manager_id: data.assignedManagerId || undefined,
        assigned_accountant_id: data.assignedAccountantId || undefined,
        budget_amount: data.budgetAmount || undefined,
        estimated_hours: data.estimatedHours || undefined,
        start_date: data.startDate || undefined,
        due_date: data.dueDate || undefined,
        is_recurring: data.isRecurring || false,
        recurrence_frequency: data.recurrenceFrequency || undefined,
        recurrence_next_date: data.recurrenceNextDate || undefined,
        dynamic_fields: data.dynamicFields || undefined,
        form_data: data.formData || undefined,
      };
      const response = await apiClient.post<ApiResponse<Job>>('/jobs', payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

/** Payload to fan a job out to selected group members (one job per member). */
export interface CreateGroupJobsRequest {
  clientIds: string[];
  allowDuplicates?: boolean;
  entityTypeId?: string;
  practiceEntityId?: string;
  jobType?: string;
  workflowTemplateId?: string;
  /** Optional: mandatory-ness is controlled by the tenant's form builder. */
  title?: string;
  description?: string;
  priority?: JobPriority;
  assignedPartnerId?: string;
  assignedManagerId?: string;
  assignedAccountantId?: string;
  budgetAmount?: number;
  estimatedHours?: number;
  startDate?: string;
  dueDate?: string;
  formData?: Record<string, unknown>;
}

export interface GroupJobsResult {
  total: number;
  successful: number;
  failed: number;
  skipped: number;
  results: Array<{ client_id: string; job_id?: string; error?: string; skipped?: boolean }>;
}

/**
 * Hook to create the same job for multiple group members (fan-out). Each member
 * gets its own individual job; they surface under the group's jobs view.
 */
export function useCreateGroupJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateGroupJobsRequest) => {
      const payload: Record<string, unknown> = {
        client_ids: data.clientIds,
        allow_duplicates: data.allowDuplicates ?? false,
        entity_type_id: data.entityTypeId || undefined,
        practice_entity_id: data.practiceEntityId || undefined,
        job_type: data.jobType || undefined,
        workflow_template_id: data.workflowTemplateId || undefined,
        title: data.title,
        description: data.description || undefined,
        priority: data.priority || undefined,
        assigned_partner_id: data.assignedPartnerId || undefined,
        assigned_manager_id: data.assignedManagerId || undefined,
        assigned_accountant_id: data.assignedAccountantId || undefined,
        budget_amount: data.budgetAmount ?? undefined,
        estimated_hours: data.estimatedHours ?? undefined,
        start_date: data.startDate || undefined,
        due_date: data.dueDate || undefined,
        form_data: data.formData || undefined,
      };
      const response = await apiClient.post<ApiResponse<GroupJobsResult>>(
        '/jobs/group-create',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

/**
 * Hook to update an existing job.
 */
export function useUpdateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateJobRequest & { id: string }) => {
      const payload: Record<string, unknown> = {};
      if (data.clientId !== undefined) payload.client_id = data.clientId;
      if (data.title !== undefined) payload.title = data.title;
      if (data.description !== undefined) payload.description = data.description;
      if (data.status !== undefined) payload.status = data.status;
      if (data.priority !== undefined) payload.priority = data.priority;
      if (data.assignedPartnerId !== undefined)
        payload.assigned_partner_id = data.assignedPartnerId;
      if (data.workflowTemplateId !== undefined)
        payload.workflow_template_id = data.workflowTemplateId;
      if (data.assignedManagerId !== undefined)
        payload.assigned_manager_id = data.assignedManagerId;
      if (data.assignedAccountantId !== undefined)
        payload.assigned_accountant_id = data.assignedAccountantId;
      if (data.budgetAmount !== undefined) payload.budget_amount = data.budgetAmount;
      if (data.estimatedHours !== undefined) payload.estimated_hours = data.estimatedHours;
      if (data.startDate !== undefined) payload.start_date = data.startDate;
      if (data.dueDate !== undefined) payload.due_date = data.dueDate;
      if (data.isRecurring !== undefined) payload.is_recurring = data.isRecurring;
      if (data.recurrenceFrequency !== undefined)
        payload.recurrence_frequency = data.recurrenceFrequency;
      if (data.recurrenceNextDate !== undefined)
        payload.recurrence_next_date = data.recurrenceNextDate;
      if (data.cancellationReason !== undefined)
        payload.cancellation_reason = data.cancellationReason;
      if (data.formData !== undefined) payload.form_data = data.formData;

      const response = await apiClient.put<ApiResponse<Job>>(`/jobs/${id}`, payload);
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.removeQueries({ queryKey: jobKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.kanban() });
    },
  });
}

export interface BulkRecurrenceRequest {
  jobIds: string[];
  isRecurring: boolean;
  recurrenceFrequency?: string | null;
  recurrenceNextDate?: string | null;
}

/**
 * Hook to update recurrence on multiple jobs.
 */
export function useBulkUpdateRecurrence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkRecurrenceRequest) => {
      const response = await apiClient.post<
        ApiResponse<{
          total: number;
          successful: number;
          failed: number;
          results: { job_id: string; success: boolean; error?: string }[];
        }>
      >('/jobs/bulk-recurrence', {
        job_ids: data.jobIds,
        is_recurring: data.isRecurring,
        recurrence_frequency: data.recurrenceFrequency,
        recurrence_next_date: data.recurrenceNextDate,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.kanban() });
    },
  });
}

/**
 * Hook to change job status (On Hold, In Review).
 * Uses PATCH /jobs/:id/status — requires job_management.change_status permission.
 */
export function useChangeJobStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, status }: { jobId: string; status: JobStatus }) => {
      const response = await apiClient.patch<ApiResponse<Job>>(`/jobs/${jobId}/status`, { status });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.removeQueries({ queryKey: jobKeys.detail(variables.jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.kanban() });
    },
  });
}

/**
 * Hook to cancel a job with a mandatory reason.
 * Uses POST /jobs/:id/cancel — the dedicated, audited cancellation path that
 * records a timeline entry (who cancelled, when, and the reason).
 * Requires job_management.change_status permission.
 */
export function useCancelJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      cancellationReason,
    }: {
      jobId: string;
      cancellationReason: string;
    }) => {
      const response = await apiClient.post<ApiResponse<Job>>(`/jobs/${jobId}/cancel`, {
        cancellation_reason: cancellationReason,
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.removeQueries({ queryKey: jobKeys.detail(variables.jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.kanban() });
    },
  });
}

/**
 * Hook to toggle assign task to client.
 * Uses PATCH /jobs/:jobId/tasks/:taskId/assign-client.
 * Requires job_management.assign_task_to_client permission.
 */
export function useAssignTaskToClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      taskId,
      assignedToClient,
      dueDate,
    }: {
      jobId: string;
      taskId: string;
      assignedToClient: boolean;
      /** Optional client due date (ISO string). Only used when assigning. */
      dueDate?: string | null;
    }) => {
      const response = await apiClient.patch<ApiResponse<{ task: JobTask; jobStatus: string }>>(
        `/jobs/${jobId}/tasks/${taskId}/assign-client`,
        { assigned_to_client: assignedToClient, due_date: dueDate ?? null },
      );
      return response.data.data;
    },
    onSuccess: (data, variables) => {
      // Surgically update only the affected task + job status in the cached
      // detail — no full refetch, so the page doesn't reload/flash.
      queryClient.setQueryData<JobDetail>(jobKeys.detail(variables.jobId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          status: (data?.jobStatus as JobDetail['status']) ?? prev.status,
          tasks: prev.tasks.map((t) =>
            t.id === variables.taskId
              ? {
                  ...t,
                  assignedToClient: variables.assignedToClient,
                  clientDueDate: variables.assignedToClient ? (variables.dueDate ?? null) : null,
                }
              : t,
          ),
        };
      });
      // Keep list/kanban views eventually consistent (background refetch, no flash).
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.kanban() });
    },
  });
}

/**
 * Hook to transition a job to the next stage.
 */
export function useTransitionStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, targetStageId }: TransitionStageRequest) => {
      const response = await apiClient.post<ApiResponse<JobDetail>>(
        `/jobs/${jobId}/stages/${targetStageId}/transition`,
      );
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(variables.jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.kanban() });
    },
  });
}

/**
 * Hook to complete a task within a job.
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, taskId }: { jobId: string; taskId: string }) => {
      const response = await apiClient.patch<ApiResponse<JobTask>>(
        `/jobs/${jobId}/tasks/${taskId}`,
        { is_completed: true },
      );
      return response.data.data;
    },
    // Await the job-detail refetch so the mutation stays `isPending` until the
    // refreshed task (with is_completed=true) has actually landed. Otherwise the
    // mutation settles as soon as the PATCH resolves — before the background
    // refetch updates the checkbox — leaving a lag window where the loader is
    // gone but the box is still unchecked and clickable (duplicate submissions).
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: jobKeys.detail(variables.jobId) });
      // The list refetch doesn't gate the checkbox UI, so it can run in the
      // background without holding the loader open.
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    },
  });
}

/**
 * Hook to create an ad-hoc task on the current stage of a job.
 * Requires job_management.create_task permission.
 */
export function useCreateAdHocTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      jobId,
      title,
      description,
      isMandatory,
      assignedToId,
      taskType,
    }: {
      jobId: string;
      title: string;
      description?: string | null;
      isMandatory?: boolean;
      assignedToId?: string | null;
      taskType?: 'standard' | 'document_upload';
    }) => {
      const payload: Record<string, unknown> = { title };
      if (description !== undefined) payload.description = description;
      if (isMandatory !== undefined) payload.is_mandatory = isMandatory;
      if (assignedToId !== undefined) payload.assigned_to_id = assignedToId;
      if (taskType !== undefined) payload.task_type = taskType;

      const response = await apiClient.post<ApiResponse<JobTask>>(`/jobs/${jobId}/tasks`, payload);
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(variables.jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    },
  });
}

/** Allowed status transition from the workflow template */
export interface AllowedTransition {
  status: JobStatus;
  stage_name: string;
  stage_id: string;
  display_order: number;
}

/** Response from the allowed-transitions endpoint */
export interface AllowedTransitionsResponse {
  current_status: JobStatus;
  allowed_transitions: AllowedTransition[];
  has_linked_statuses: boolean;
}

/**
 * Hook to fetch allowed status transitions for a job based on its workflow template.
 * When the template has linked_status values on stages, only those statuses are valid transitions.
 */
export function useAllowedTransitions(jobId: string) {
  return useQuery({
    queryKey: [...jobKeys.detail(jobId), 'allowed-transitions'] as const,
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: AllowedTransitionsResponse;
      }>(`/jobs/${jobId}/allowed-transitions`);
      return response.data.data;
    },
    enabled: !!jobId,
  });
}
